// 🔧 store.js
// ✅ Purpose: Public-facing Store catalog for an event
// ✅ Features:
// - Loads active Store products from /events/{eventId}/store_items
// - Loads active variants for each product
// - Shows product cards grouped by category
// - Shows non-interactive product option badges
// - Links shoppers to product details and Merchant Cart checkout
// - Fully block-commented for clarity and future devs

import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  orderBy,
  query,
  doc,
  getDoc,
} from "firebase/firestore";
import { Link } from "react-router-dom";
import { db } from "../../firebase";
import { useEventContext } from "../../hooks/use_event_context";
import FullPageSpinner from "../../components/FullPageSpinner";
import { logger } from "../../utils/logger";
import { hasPurchasedAddOn } from "../../utils/event_addons";
import AddonRequired from "../../components/AddonRequired";
import SeoHead from "../../components/seo_head";
import { buildEventPageSeo } from "../../utils/event_page_seo_helpers";

const Store = () => {

  const {
    eventslug,
    eventId,
    eventDoc,
    eventLoading,
    ready,
  } = useEventContext({ redirectIfNotFound: true });

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [storeSettings, setStoreSettings] = useState(null);
  const storeEnabled = hasPurchasedAddOn(
    eventDoc,
    "store"
  );  
  useEffect(() => {
    const loadStore = async () => {
      if (!eventId) return;

      setLoading(true);

      const settingsSnap = await getDoc(
        doc(db, "events", eventId, "settings", "store")
        );

        const settingsData = settingsSnap.exists() ? settingsSnap.data() : {};

        setStoreSettings(settingsData);

        if (settingsData.storeEnabled === false) {
        setProducts([]);
        setLoading(false);
        return;
        }

      try {
        logger.log("🛒 Loading public Store catalog", { eventId });

        const productsSnap = await getDocs(
          query(
            collection(db, "events", eventId, "store_items"),
            orderBy("displayOrder", "asc")
          )
        );

        const productList = [];

        for (const productDoc of productsSnap.docs) {
          const product = {
            id: productDoc.id,
            ...productDoc.data(),
          };

          if (product.active === false) continue;

          const variantsSnap = await getDocs(
            query(
              collection(
                db,
                "events",
                eventId,
                "store_items",
                productDoc.id,
                "variants"
              ),
              orderBy("label", "asc")
            )
          );

          const variants = variantsSnap.docs
            .map((variantDoc) => ({
              id: variantDoc.id,
              ...variantDoc.data(),
            }))
            .filter((variant) => variant.active !== false);

          productList.push({
            ...product,
            variants,
          });
        }

        setProducts(productList);

        logger.log("✅ Public Store catalog loaded", {
          eventId,
          count: productList.length,
        });
      } catch (err) {
        logger.error("❌ Error loading public Store catalog:", {
          value: err,
        });

        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    loadStore();
  }, [eventId]);

  const productsByCategory = [
    ...new Set(
      products.map((product) => product.category || "Store Items")
    ),
  ].map((categoryName) => ({
    categoryName,
    products: products.filter(
      (product) => (product.category || "Store Items") === categoryName
    ),
  }));

  const storeSeo = {
    ...buildEventPageSeo({
      eventDoc,
      pageKey: "store",

      fallbackTitle:
        `${eventDoc?.name || "Event"} Store | Let's Go Vote`,

      fallbackDescription:
        storeSettings?.storeDescription ||
        `Shop merchandise for ${
          eventDoc?.name || "this event"
        }.`,
    }),

    path: `/${eventslug}/store`,

    image:
      storeSettings?.storeBannerImageUrl ||
      eventDoc?.eventLogo,

    breadcrumbs: [
      { name: "Home", path: "/" },
      {
        name: eventDoc?.name || "Event",
        path: `/${eventslug}/event`,
      },
      {
        name: "Store",
        path: `/${eventslug}/store`,
      },
    ],
  };

  if (
    ready &&
    eventDoc &&
    !storeEnabled
  ) {
    return (
      <AddonRequired
        eventslug={eventslug}
        title="Event Merch Store Add-on Required"
        description="This event has not purchased the Event Merch Store add-on."
      />
    );
  }
  
  if (!ready || !eventId || eventLoading || loading) {
    return <FullPageSpinner message="Loading Store..." />;
  }

  return (
  <>
    <SeoHead {...storeSeo} />
    <section id="top-content" className="container-fluid py-5">
      <div className="container" style={{ maxWidth: "1200px" }}>
        <div className="row mb-4">
          <div className="col text-start">
            <h1 className="display-5">
              🛒 Store
            </h1>

            <p className="lead text-muted mb-0">
              {eventDoc?.name
                ? `Browse merchandise for ${eventDoc.name}.`
                : "Browse event merchandise."}
            </p>
          </div>
        </div>

        {storeSettings?.storeBannerImageUrl && (
        <div className="card shadow-sm border-0 mb-4">
            <img
            src={storeSettings.storeBannerImageUrl}
            alt={`${eventDoc?.name || "Event"} Store banner`}
            className="img-fluid rounded"
            style={{
                maxHeight: "320px",
                width: "100%",
                objectFit: "cover",
            }}
            />
        </div>
        )}

        {storeSettings?.storeDescription && (
        <div className="card shadow-sm border-0 mb-4">
            <div className="card-body">
            <p className="mb-0">
                {storeSettings.storeDescription}
            </p>
            </div>
        </div>
        )}

        <div className="mb-4">
          <Link to={`/${eventslug}/cart`} className="btn btn-outline-primary">
            Merchant Cart Checkout
          </Link>
        </div>

        {products.length === 0 ? (
          <div className="card shadow-sm border-0">
            <div className="card-body">
              <div className="alert alert-info mb-0">
                No Store products are available yet.
              </div>
            </div>
          </div>
        ) : (
          productsByCategory.map((group) => (
            <div key={group.categoryName} className="mb-4">
              <div className="card shadow-sm border-0">
                <div className="card-header bg-white d-flex flex-column flex-md-row justify-content-between gap-2">
                  <div>
                    <strong>🛍 {group.categoryName}</strong>
                    <div className="small text-muted">
                      Available Store items.
                    </div>
                  </div>

                  <span className="badge bg-dark align-self-start align-self-md-center">
                    {group.products.length} Product
                    {group.products.length === 1 ? "" : "s"}
                  </span>
                </div>

                <div className="card-body">
                  <div className="row g-4">
                    {group.products.map((product) => (
                      <div key={product.id} className="col-12 col-md-6 col-xl-4">
                        <div className="card h-100 border shadow-sm">
                          <div className="card-body d-flex flex-column">
                            <div
                              className="d-flex align-items-center justify-content-center bg-light border rounded mb-3"
                              style={{ minHeight: "170px" }}
                            >
                              {product.imageUrl ? (
                                <img
                                  src={product.imageUrl}
                                  alt={product.name || "Store product"}
                                  className="img-fluid"
                                  style={{
                                    maxHeight: "155px",
                                    objectFit: "contain",
                                  }}
                                />
                              ) : (
                                <div className="text-muted small">
                                  No Image
                                </div>
                              )}
                            </div>

                            <h5 className="fw-bold mb-2">
                              {product.name || "Unnamed Product"}
                            </h5>

                            <div className="mb-3">
                              {product.featured && (
                                <span className="badge bg-warning text-dark me-2">
                                  Featured
                                </span>
                              )}

                            </div>

                            {product.description && (
                              <p className="text-muted small">
                                {product.description}
                              </p>
                            )}

                            {product.variants.length > 0 && (
                              <div className="mb-3">
                                <div className="fw-bold small mb-2">
                                  Options
                                </div>

                                <div className="d-flex flex-wrap gap-2">
                                  {product.variants.map((variant) => (
                                    <span
                                      key={variant.id}
                                      className={`badge ${
                                        Number(variant.availableInventory || 0) > 0
                                            ? "bg-light text-dark border"
                                            : "bg-secondary"
                                      }`}
                                    >
                                      {variant.label}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            <div className="d-grid gap-2 mt-auto">
                              <Link
                                to={`/${eventslug}/store/${product.productSlug || product.id}`}
                                className="btn btn-outline-secondary btn-sm"
                              >
                                View Details
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}

        <div className="text-center mt-4">
          <Link
            to={`/${eventslug}/event`}
            className="btn btn-outline-secondary"
          >
            ← Back to Event Home
          </Link>
        </div>
      </div>
    </section>
  </>
  );
};

export default Store;
